/**
 * Studies API client (authed). Mirrors the backend `app/studies` schemas.
 *
 * The researcher creates an empty draft (`createDraftStudy`), then co-authors it with the agent
 * in chat (the goals seed the first chat message, not the document). The panel sends user edits
 * as `StudyOp`s to `patchStudy` (the edit site knows the op; the backend never diffs to derive it).
 */

import { apiRequest } from "@lato/client/lib/apiClient";
import type {
  StudyAudienceSegment,
  StudyHypothesis,
  StudyHypothesisImportance,
  StudyKeyQuestion,
  StudyObjective,
  StudyScreenerQuestion,
  StudyTimeline,
} from "@/chat";
import { languageQuery, type StartInterviewResponse } from "@/features/interview/api";
import type { GenerationProgress } from "@/lib/generationProgress";
import type * as Api from "@/api/generated/types.gen";

/** Lifecycle state machine: draft/requested → live ⇄ completed (collection stopped). */
export type StudyStatus = Api.StudyStatus;
/** How the caller reaches a study: through its owning org (or as staff), or through a share. */
export type StudyAccess = Api.StudyAccess;
export type StudyFinalizationStatus = Api.StudyFinalizationStatus;
export type IntakeStatus = "idle" | "pending" | "running" | "done" | "failed";

export interface StudyIntake {
  external_title: string;
  external_description: string;
  screener: StudyScreenerQuestion[];
  status: IntakeStatus;
  generated_at: string | null;
  scope_hash: string | null;
  scope_changed: boolean;
  error: string | null;
}

/** An authored respondent group used for interview classification and segment-level reporting. */
export type AudienceSegment = StudyAudienceSegment;

export interface StudyResponse {
  id: string;
  /** Owning org — a visible study property (the study id is what the server authorizes). */
  org_id: string;
  /** How the caller reaches it; a viewer's writes are refused by the server. */
  access: StudyAccess;
  /** Whether the owning org has full studies access (launch and live actions), whoever reads it. */
  full_access: boolean;
  title: string;
  context: string;
  /** What the voice interviewer reads as background instead of `context`; empty means it reads
   *  `context`. Written to be safe to say to a participant. */
  interviewer_brief: string;
  /** What is true of every participant regardless of segment; empty when segments say it. */
  audience_description: string;
  /** Participant-facing copy shown on the interview start page (internal title/context stay agent-only). */
  external_title: string;
  external_description: string;
  key_questions: StudyKeyQuestion[];
  hypotheses: StudyHypothesis[];
  objectives: StudyObjective[];
  /** Frozen scope v1/v2 field, retained only when displaying a historical revision. */
  audience_criteria: string[];
  /** Authored respondent groups. A new draft is empty; preview/launch requires at least one. */
  audience_segments: AudienceSegment[];
  /** Target sample size: how many interviews the study aims for. A positive integer; informational
   *  only (no auto-close). */
  target_interviews: number;
  /** Geographic targeting: canonical country names participants should be from. Empty = worldwide. */
  countries: string[];
  /** When the customer needs results and what for; null when the customer gave none. */
  timeline: StudyTimeline | null;
  screener: StudyScreenerQuestion[];
  conversation_id: string | null;
  status: StudyStatus;
  /** Current HEAD version; echoed back as `base_seq` on the next edit (optimistic concurrency). */
  head_seq: number;
  scope_draft_schema_version: number;
  /** Format used by the active/presented scope (may differ from an upgraded draft). */
  scope_schema_version: number;
  scope_draft_base_revision_id: string | null;
  active_scope_revision_id: string | null;
  launched_at: string | null;
  closed_at: string | null;
  completed_at: string | null;
  finalization_status: StudyFinalizationStatus;
  finalization_requested_at: string | null;
  finalization_deadline_at: string | null;
  finalization_frozen_at: string | null;
  finalization_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface StudyPatchResponse extends StudyResponse {
  /** Exact sequence created by this idempotency key; older than HEAD on a later replay. */
  acknowledged_edit_seq: number | null;
}

export type ScopeDocument = Pick<
  StudyResponse,
  | "title"
  | "context"
  | "interviewer_brief"
  | "audience_description"
  | "key_questions"
  | "hypotheses"
  | "objectives"
  | "audience_criteria"
  | "audience_segments"
  | "countries"
  | "timeline"
> & {
  /** Frozen v1/v2 global target. V3 stores targets on audience segments instead. */
  target_interviews?: number;
};

export interface VersionedScopeDocument {
  schema_version: number;
  /** Raw authored shape for that exact version; callers normalize the absent diligence field. */
  content: Partial<ScopeDocument>;
}

export interface ScopeRevision {
  id: string;
  revision: number;
  schema_version: number;
  content_hash: string;
  parent_revision_id: string | null;
  diff: Record<string, unknown>;
  supersedes: Record<string, string>;
  publication_note: string | null;
  published_by: string | null;
  published_at: string;
}

export interface ScopeWorkspace {
  draft: VersionedScopeDocument;
  draft_base_revision_id: string | null;
  head_seq: number;
  active_revision: ScopeRevision | null;
  active_scope: VersionedScopeDocument | null;
  has_unpublished_changes: boolean;
}

export interface ScopeUpgradePreview {
  from_schema_version: number;
  to_schema_version: number;
  affected_sections: string[];
  summaries: string[];
  upgraded_scope: VersionedScopeDocument;
}

/** What a researcher action did to the study's provisioned moderator. `failed` carries a
 *  content-free reason safe to show; `disabled` means the study does not use one. */
export interface ModeratorStatus {
  status: "ready" | "failed" | "disabled";
  error_type: string | null;
  error_reason: string | null;
}

/** A launch or reopen: the study plus what the action did to its provisioned moderator. */
export interface StudyTransitionResult extends StudyResponse {
  moderator?: ModeratorStatus | null;
}

export interface ScopePublishResult extends ScopeWorkspace {
  created: boolean;
  /** Absent against a backend predating it. */
  moderator?: ModeratorStatus | null;
}

/** A study row on the /studies list and board (the backend's `StudyListItem`). */
export type StudyListItem = Api.StudyListItem;

/** Someone following a study, as its board card shows them (the backend's `UserIdentity`). */
export type BoardFollower = Api.UserIdentity;

/** One audience segment on a board card: completed interviews against its target. */
export type BoardSegment = Api.BoardSegment;

export interface DraftStudyResponse {
  study_id: string;
  conversation_id: string;
}

/** Drives board progress badges and poll-while-finalizing refresh. */
export function isGenerating(s: StudyListItem): boolean {
  return s.status === "completed" && s.finalization_status === "waiting";
}

// ── The StudyOp vocabulary (the panel emits these at the edit site) ───────────

export type StudyFieldName = "title" | "context" | "interviewer_brief" | "audience_description";

export type StudyOp =
  | { op: "set_field"; field: StudyFieldName; value: string }
  | { op: "set_countries"; countries: string[] }
  | { op: "set_timeline"; timeline: StudyTimeline | null }
  | { op: "add_audience_segment"; segment: StudyAudienceSegment }
  | {
      op: "update_audience_segment";
      id: string;
      label?: string;
      description?: string;
      target_interviews?: number;
      example_companies?: StudyAudienceSegment["example_companies"];
    }
  | { op: "remove_audience_segment"; id: string }
  | { op: "add_key_question"; key_question: StudyKeyQuestion }
  | {
      op: "update_key_question";
      id: string;
      question?: string;
      proof_points?: string;
    }
  | { op: "remove_key_question"; id: string }
  | { op: "add_hypothesis"; hypothesis: StudyHypothesis }
  | {
      op: "update_hypothesis";
      id: string;
      hypothesis?: string;
      /** `""` clears the level; omitted leaves it unchanged. */
      importance?: StudyHypothesisImportance | "";
    }
  | { op: "remove_hypothesis"; id: string }
  | {
      op: "add_hypothesis_key_question";
      hypothesis_id: string;
      key_question: StudyHypothesis["key_questions"][number];
    }
  | {
      op: "update_hypothesis_key_question";
      hypothesis_id: string;
      id: string;
      question?: string;
    }
  | { op: "remove_hypothesis_key_question"; hypothesis_id: string; id: string }
  | { op: "add_objective"; objective: StudyObjective }
  | {
      op: "update_objective";
      id: string;
      goal?: string;
      question?: string;
      follow_ups?: string[];
      done_when?: string | null;
      segment_ids?: string[];
    }
  | { op: "remove_objective"; id: string }
  | { op: "reorder_audience_segments"; ids: string[] }
  | { op: "reorder_key_questions"; ids: string[] }
  | { op: "reorder_hypotheses"; ids: string[] }
  | { op: "reorder_hypothesis_key_questions"; hypothesis_id: string; ids: string[] }
  | { op: "reorder_objectives"; ids: string[] };

/** The JSON `detail` of a 409 study-edit conflict (the fields a concurrent edit changed). */
export interface StudyConflictDetail {
  message: string;
  changed_fields: string[];
  head_seq: number;
}

/** A failed studies request, carrying the HTTP status (and the parsed `detail`, e.g. a 409
 *  conflict payload) so callers can tailor error copy.
 *
 *  When the backend sent a plain-string `detail` it *is* the user-facing message, so it becomes
 *  `message` — callers that just toast `error.message` show the real reason instead of a status
 *  code. A structured `detail` (a 409 conflict payload, FastAPI's 422 validation array) is not
 *  displayable copy, so those keep the generic line and callers branch on `detail` themselves. */
export class StudiesRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail?: unknown
  ) {
    super(typeof detail === "string" && detail ? detail : `Studies request failed (${status})`);
  }
}

export async function json<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    let detail: unknown;
    try {
      detail = ((await resp.json()) as { detail?: unknown })?.detail;
    } catch {
      // no JSON body
    }
    throw new StudiesRequestError(resp.status, detail);
  }
  return (await resp.json()) as T;
}

export async function listStudies(): Promise<StudyListItem[]> {
  return json(await apiRequest("/studies"));
}

export async function getStudy(studyId: string, speculative = false): Promise<StudyResponse> {
  return json(await apiRequest(`/studies/${studyId}`, { speculative }));
}

export interface CodedMatrixEvidence {
  turn_index: number;
  quote: string;
}

export interface CodedMatrixCode extends CodedMatrixEvidence {
  code_id: string;
}

export interface CodedMatrixCell {
  state: "coded" | "unfitted" | "not_addressed";
  codes: CodedMatrixCode[];
  quote: CodedMatrixEvidence | null;
  summary: string;
}

export interface CodedMatrixColumn {
  key_ref: string;
  hypothesis_id: string;
  hypothesis: string;
  question_id: string;
  question: string;
  family_id: string;
  label: string;
  family_definition: string;
  selection_type: "single" | "multi";
  eligible_segments?: string[];
  eligible_segment_labels?: Record<string, string>;
  code_labels: Record<string, string>;
  code_definitions: Record<string, string>;
  counts: Record<string, number>;
  base: number;
  unfitted: number;
  not_addressed: number;
  ineligible?: number;
}

export interface CodedMatrixRow {
  session_id: string;
  role: string;
  /** Absent on payloads cached before the field shipped. */
  company?: string;
  country: string;
  segment: string;
  /** Start offset (seconds) of every transcript turn by index; null where the provider left no
   *  stamp, empty for a transcript without timing. Places a cell's `turn_index` in the call. */
  turn_offsets?: (number | null)[];
  cells: Record<string, CodedMatrixCell>;
}

export interface CodedMatrix {
  codebook_revision_id: string | null;
  /** Latest saved coding timestamp; absent on older responses. */
  generated_at?: string | null;
  columns: CodedMatrixColumn[];
  rows: CodedMatrixRow[];
}

export interface QuestionAnswerCitation {
  /** Absent on stored rows written before secondary evidence shipped; those rows are interviews. */
  kind?: "interview" | "secondary";
  handle: string;
  session_id: string | null;
  turn_index: number | null;
  quote: string;
  family_id: string | null;
  code_id: string | null;
  atom_id?: string | null;
  source_id?: string | null;
  url?: string | null;
  title?: string | null;
  publisher?: string | null;
  published_date?: string | null;
  suitability?: string | null;
  screenshot_id?: string | null;
  text_start?: string | null;
  text_end?: string | null;
  deeplinkable?: boolean | null;
}

export interface QuestionAnswerMiss {
  kind: "unfitted_evidence" | "conflicting_evidence" | "insufficient_evidence" | "other";
  detail: string;
  handles: string[];
}

export interface QuestionAnswerContent {
  headline: string;
  finding: string;
  citations: QuestionAnswerCitation[];
  misses: QuestionAnswerMiss[];
  handles: Record<string, string>;
}

export interface QuestionAnswerRead {
  key_ref: string;
  question: string;
  codebook_revision_id: string;
  is_stale: boolean;
  generated_at: string;
  answer: QuestionAnswerContent;
}

export interface StudyQuestionAnswers {
  codebook_revision_id: string | null;
  answers: QuestionAnswerRead[];
}

export type HypothesisVerdict = "supported" | "not_supported" | "contested" | "insufficient";

export type VerdictConfidence = "high" | "medium" | "low";

export type HypothesisOpenItemKind =
  | "interview_gap"
  | "secondary_gap"
  | "company_data"
  | "method_limit"
  | "other";

export interface HypothesisOpenItem {
  kind: HypothesisOpenItemKind;
  detail: string;
}

export interface HypothesisAnswer {
  verdict: HypothesisVerdict;
  confidence: VerdictConfidence;
  headline: string;
  answer: string;
  supporting: string[];
  counterpoint: string[];
  implication: string;
  open_items: HypothesisOpenItem[];
  citations: QuestionAnswerCitation[];
  handles: Record<string, string>;
}

export interface HypothesisAnswerRead {
  hypothesis_id: string;
  hypothesis: string;
  codebook_revision_id: string;
  is_stale: boolean;
  generated_at: string;
  answer: HypothesisAnswer;
}

export interface StudyHypothesisAnswers {
  codebook_revision_id: string | null;
  answers: HypothesisAnswerRead[];
}

export interface ExecutiveSummary {
  thesis: string;
  overview: string;
  findings: string[];
  implications: string[];
  open_items: string[];
  citations: QuestionAnswerCitation[];
  handles: Record<string, string>;
}

export interface ExecutiveSummaryRead {
  codebook_revision_id: string;
  is_stale: boolean;
  generated_at: string;
  summary: ExecutiveSummary;
}

export interface StudyExecutiveSummary {
  codebook_revision_id: string | null;
  summary: ExecutiveSummaryRead | null;
}

export async function getCodedMatrix(studyId: string): Promise<CodedMatrix> {
  return json(await apiRequest(`/studies/${studyId}/coding/matrix`));
}

export async function getQuestionAnswers(studyId: string): Promise<StudyQuestionAnswers> {
  return json(await apiRequest(`/studies/${studyId}/coding/answers`));
}

export async function getHypothesisAnswers(studyId: string): Promise<StudyHypothesisAnswers> {
  return json(await apiRequest(`/studies/${studyId}/coding/hypotheses`));
}

export async function getExecutiveSummary(studyId: string): Promise<StudyExecutiveSummary> {
  return json(await apiRequest(`/studies/${studyId}/coding/summary`));
}

export interface CodebookAuthoringAccepted {
  status: "accepted";
  workflow_id: string;
}

export interface StudyCodingAccepted {
  status: "accepted";
  workflow_ids: string[];
}

export interface QuestionAnswersAccepted {
  status: "accepted";
  workflow_ids: string[];
}

/** Queue staff-only codebook authoring. Every invocation is a distinct request. */
export async function requestCodebookAuthoring(
  studyId: string,
  { fromScratch }: { fromScratch: boolean }
): Promise<CodebookAuthoringAccepted> {
  return json(
    await apiRequest(`/studies/${studyId}/codebook/author`, {
      method: "POST",
      body: JSON.stringify({
        idempotency_key: crypto.randomUUID(),
        from_scratch: fromScratch,
      }),
    })
  );
}

/** Queue interview coding. Every invocation is a distinct request. */
export async function requestStudyCoding(
  studyId: string,
  { fromScratch }: { fromScratch: boolean }
): Promise<StudyCodingAccepted> {
  return json(
    await apiRequest(`/studies/${studyId}/coding/run`, {
      method: "POST",
      body: JSON.stringify({
        idempotency_key: crypto.randomUUID(),
        from_scratch: fromScratch,
      }),
    })
  );
}

/** Queue key-question answers. Every invocation is a distinct request. */
export async function requestQuestionAnswers(
  studyId: string,
  { fromScratch }: { fromScratch: boolean }
): Promise<QuestionAnswersAccepted> {
  return json(
    await apiRequest(`/studies/${studyId}/coding/answers/run`, {
      method: "POST",
      body: JSON.stringify({
        idempotency_key: crypto.randomUUID(),
        from_scratch: fromScratch,
      }),
    })
  );
}

/** Queue hypothesis adjudications. Every invocation is a distinct request. */
export async function requestHypothesisAnswers(
  studyId: string,
  { fromScratch }: { fromScratch: boolean }
): Promise<QuestionAnswersAccepted> {
  return json(
    await apiRequest(`/studies/${studyId}/coding/hypotheses/run`, {
      method: "POST",
      body: JSON.stringify({
        idempotency_key: crypto.randomUUID(),
        from_scratch: fromScratch,
      }),
    })
  );
}

/** Queue the study-level executive summary. Every invocation is a distinct request. */
export async function requestExecutiveSummary(
  studyId: string,
  { fromScratch }: { fromScratch: boolean }
): Promise<QuestionAnswersAccepted> {
  return json(
    await apiRequest(`/studies/${studyId}/coding/summary/run`, {
      method: "POST",
      body: JSON.stringify({
        idempotency_key: crypto.randomUUID(),
        from_scratch: fromScratch,
      }),
    })
  );
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

/** Filename the server chose (Content-Disposition), or the fallback when it sent none. */
export function attachmentFilename(contentDisposition: string | null, fallback: string): string {
  const match = contentDisposition?.match(/filename="?([^";]+)"?/);
  return match?.[1]?.trim() || fallback;
}

async function downloadStudyFile(path: string, fallback: string): Promise<DownloadedFile> {
  const resp = await apiRequest(path);
  if (!resp.ok) {
    let detail: unknown;
    try {
      detail = ((await resp.json()) as { detail?: unknown })?.detail;
    } catch {
      // no JSON body
    }
    throw new StudiesRequestError(resp.status, detail);
  }
  return {
    blob: await resp.blob(),
    filename: attachmentFilename(resp.headers.get("content-disposition"), fallback),
  };
}

/** The coding workbook: Instructions, Matrix, Evidence sheets for the study's coded matrix. */
export function downloadCodingWorkbook(studyId: string): Promise<DownloadedFile> {
  return downloadStudyFile(`/studies/${studyId}/coding/export.xlsx`, "coding.xlsx");
}

/** The client-ready study findings deck as an editable PowerPoint presentation. */
export function downloadStudyDeck(studyId: string): Promise<DownloadedFile> {
  return downloadStudyFile(`/studies/${studyId}/deliverables/deck.pptx`, "deck.pptx");
}

/** The same deck as the PowerPoint, as a PDF. */
export function downloadStudyDeckPdf(studyId: string): Promise<DownloadedFile> {
  return downloadStudyFile(`/studies/${studyId}/deliverables/deck.pdf`, "deck.pdf");
}

/** Every qualified interview's transcript in one document, as shown in the app. */
export function downloadStudyTranscripts(
  studyId: string,
  format: "docx" | "pdf"
): Promise<DownloadedFile> {
  return downloadStudyFile(
    `/studies/${studyId}/deliverables/transcripts.${format}`,
    `transcripts.${format}`
  );
}

export async function getStudyScope(studyId: string): Promise<ScopeWorkspace> {
  return json(await apiRequest(`/studies/${studyId}/scope`));
}

export async function getScopeUpgradePreview(studyId: string): Promise<ScopeUpgradePreview> {
  return json(await apiRequest(`/studies/${studyId}/scope/upgrade-preview`));
}

export async function upgradeStudyScope(
  studyId: string,
  workspace: ScopeWorkspace
): Promise<ScopeWorkspace> {
  return json(
    await apiRequest(`/studies/${studyId}/scope/upgrade`, {
      method: "POST",
      body: JSON.stringify({
        expected_head_seq: workspace.head_seq,
        expected_active_revision_id: workspace.active_revision?.id ?? null,
        client_upgrade_id: crypto.randomUUID(),
      }),
    })
  );
}

export async function publishStudyScope(
  studyId: string,
  workspace: ScopeWorkspace,
  publicationNote?: string
): Promise<ScopePublishResult> {
  return json(
    await apiRequest(`/studies/${studyId}/scope/publish`, {
      method: "POST",
      body: JSON.stringify({
        expected_head_seq: workspace.head_seq,
        expected_active_revision_id: workspace.active_revision?.id ?? null,
        client_publish_id: crypto.randomUUID(),
        publication_note: publicationNote,
      }),
    })
  );
}

/** Re-run moderator provisioning for the active revision: the retry behind a `failed` status. */
export async function provisionStudyModerator(studyId: string): Promise<ModeratorStatus> {
  return json(await apiRequest(`/studies/${studyId}/moderator/provision`, { method: "POST" }));
}

export async function discardStudyScope(
  studyId: string,
  workspace: ScopeWorkspace
): Promise<ScopeWorkspace> {
  return json(
    await apiRequest(`/studies/${studyId}/scope/discard`, {
      method: "POST",
      body: JSON.stringify({
        expected_head_seq: workspace.head_seq,
        expected_active_revision_id: workspace.active_revision?.id ?? null,
        client_edit_id: crypto.randomUUID(),
      }),
    })
  );
}

/**
 * Create the draft under client-minted ids (the optimistic new-study flow): the browser navigates
 * + renders + seeds its caches in frame 1, then persists here in the background. The full study is
 * returned so the caller can reconcile its `["study", id]` cache with server truth. Idempotent —
 * a retry/double-submit with the same ids returns the existing study.
 */
export async function createDraftStudy(
  studyId: string,
  conversationId: string,
  /** The owning org, chosen explicitly (required for staff; members may omit it). */
  orgId?: string
): Promise<StudyResponse> {
  return json(
    await apiRequest("/studies/drafts", {
      method: "POST",
      body: JSON.stringify({
        study_id: studyId,
        conversation_id: conversationId,
        ...(orgId ? { org_id: orgId } : {}),
      }),
    })
  );
}

/**
 * A client-side blank draft, shaped exactly like the server's `StudyResponse`, so a freshly minted
 * study renders the authoring view instantly — before `POST /studies/drafts` lands. The empty
 * fields are the server's own defaults for a new draft, so this is truthful, not a guess; it's
 * reconciled with the server response on success regardless.
 */
export function blankDraftStudy(id: string, conversationId: string): StudyResponse {
  const now = new Date().toISOString();
  return {
    id,
    // Unknown until the server persists the draft (reconciled on success).
    org_id: "",
    // A draft is the caller's own; its org's entitlement arrives with the server's response.
    access: "owner",
    full_access: false,
    title: "",
    context: "",
    interviewer_brief: "",
    audience_description: "",
    external_title: "",
    external_description: "",
    key_questions: [],
    hypotheses: [],
    objectives: [],
    audience_criteria: [],
    audience_segments: [],
    target_interviews: 10,
    countries: [],
    timeline: null,
    screener: [],
    conversation_id: conversationId,
    status: "draft",
    head_seq: 0,
    scope_draft_schema_version: 3,
    scope_schema_version: 3,
    scope_draft_base_revision_id: null,
    active_scope_revision_id: null,
    launched_at: null,
    closed_at: null,
    completed_at: null,
    finalization_status: "idle",
    finalization_requested_at: null,
    finalization_deadline_at: null,
    finalization_frozen_at: null,
    finalization_error: null,
    created_at: now,
    updated_at: now,
  };
}

/**
 * A synthesized study-chat row so `ArtifactChatPane` picks the client-minted conversation as
 * the active chat immediately — without waiting on `GET /studies/{id}/conversations` (and without
 * its empty-list auto-create firing). The pane reduces to newest-by-`updated_at` and reads only
 * `conversation_id`/`title`/`preview`.
 */
export function blankArtifactChat(conversationId: string): ArtifactChat {
  const now = new Date().toISOString();
  return {
    conversation_id: conversationId,
    agent_id: "",
    title: null,
    preview: null,
    status: "ready",
    error_message: null,
    created_at: now,
    updated_at: now,
  };
}

/** Duplicate a study's scope into a fresh draft (+ its own authoring chat). `orgId` names the
 *  owning org of the copy explicitly (staff keep a customer's copy in that customer's org); without
 *  it the copy lands in the caller's current org. The server validates the selector. */
export async function copyStudy(studyId: string, orgId?: string): Promise<DraftStudyResponse> {
  return json(
    await apiRequest(`/studies/${studyId}/copy`, {
      method: "POST",
      ...(orgId ? { body: JSON.stringify({ org_id: orgId }) } : {}),
    })
  );
}

/** Archive a study: hide it from the board unless archived studies are shown. */
export async function archiveStudy(studyId: string): Promise<StudyResponse> {
  return json(await apiRequest(`/studies/${studyId}/archive`, { method: "POST" }));
}

/** Restore an archived study to the board. */
export async function unarchiveStudy(studyId: string): Promise<StudyResponse> {
  return json(await apiRequest(`/studies/${studyId}/unarchive`, { method: "POST" }));
}

/** Drag to reorder on the caller's org board: the study goes below `before_id` and above
 *  `after_id`, the cards now beside it in its column (null at an end). The server derives the
 *  position; the order is the org's, shared by its members. */
export async function moveStudyOnBoard(
  studyId: string,
  placement: { before_id: string | null; after_id: string | null }
): Promise<void> {
  const response = await apiRequest(`/studies/${studyId}/board-position`, {
    method: "PUT",
    body: JSON.stringify(placement),
  });
  // A 204 has no body; `json` still owns the error path.
  if (!response.ok) await json(response);
}

// ── Study-scoped chats ───────────────────────────────────────────────────────
// Each study has one per-user chat collection across its full lifecycle. These mirror the backend
// `RecentConversation` shape so the chat-history surface reuses the web sidebar's row data.

/** One chat row in a study chat-history list (mirrors backend `RecentConversation`). */
export interface ArtifactChat {
  conversation_id: string;
  agent_id: string;
  title: string | null;
  preview: string | null;
  status: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

/** The caller's chats for a study across Draft, Live, and Completed (newest first). */
export async function listStudyConversations(studyId: string): Promise<ArtifactChat[]> {
  return json(await apiRequest(`/studies/${studyId}/conversations`));
}

/**
 * Materialise a study chat under a client-minted id (the deferred new-chat flow: "+"
 * persists nothing; the first message creates the chat). Idempotent under retry.
 */
export async function createStudyConversation(
  studyId: string,
  conversationId: string
): Promise<ArtifactChat> {
  return json(
    await apiRequest(`/studies/${studyId}/conversations`, {
      method: "POST",
      body: JSON.stringify({ conversation_id: conversationId }),
    })
  );
}

/** Rename a chat. */
export async function renameConversation(conversationId: string, title: string): Promise<void> {
  const res = await apiRequest(`/conversations/${conversationId}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error("Failed to rename chat");
}

/** Delete a chat. */
export async function deleteConversation(conversationId: string): Promise<void> {
  const res = await apiRequest(`/conversations/${conversationId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete chat");
}

/** Auto-name a chat from its first message (best-effort; mirrors the web chat title flow). */
export async function generateConversationTitle(
  conversationId: string,
  message: string
): Promise<{ conversation_id: string; title: string | null }> {
  return json(
    await apiRequest(`/conversations/${conversationId}/generate-title`, {
      method: "POST",
      body: JSON.stringify({ message }),
    })
  );
}

/**
 * Start a researcher preview of the interview (authed). The backend mints an ephemeral session
 * (no DB row), so it runs identically to a real interview but is never counted as real data.
 */
export async function startPreviewInterview(
  studyId: string,
  language?: string
): Promise<StartInterviewResponse> {
  return json(
    await apiRequest(`/interviews/studies/${studyId}/preview${languageQuery(language)}`, {
      method: "POST",
    })
  );
}

/** Staff-owned default participant welcome copy and screening for every link in a study. */
export async function getStudyIntake(studyId: string): Promise<StudyIntake> {
  return json(await apiRequest(`/studies/${studyId}/intake`));
}

export async function updateStudyIntake(
  studyId: string,
  intake: Pick<StudyIntake, "external_title" | "external_description" | "screener">
): Promise<StudyIntake> {
  return json(
    await apiRequest(`/studies/${studyId}/intake`, {
      method: "PUT",
      body: JSON.stringify(intake),
    })
  );
}

export async function regenerateStudyIntake(
  studyId: string,
  target?: { distribution_id: string; segment_id?: string }
): Promise<{ status: IntakeStatus; attempt_at: string }> {
  return json(
    await apiRequest(`/studies/${studyId}/intake/regenerate`, {
      method: "POST",
      ...(target ? { body: JSON.stringify(target) } : {}),
    })
  );
}

export async function patchStudy(
  studyId: string,
  ops: StudyOp[],
  clientEditId?: string,
  baseSeq?: number,
  acknowledgedSeqs?: readonly number[]
): Promise<StudyPatchResponse> {
  return json(
    await apiRequest(`/studies/${studyId}`, {
      method: "PATCH",
      // client_edit_id is an idempotency key: a retried/double-submitted PATCH reusing the same id
      // lands exactly one edit server-side instead of racing to a duplicate seq (500). base_seq is
      // the HEAD the client last saw — the server 409s if a concurrent edit touched an overlapping
      // field since then, and auto-merges disjoint edits. acknowledged_seqs are this client's own
      // acknowledged revisions, which the server excludes from that conflict scan: a queued edit
      // should never 409 against the caller's own previous gesture.
      body: JSON.stringify({
        ops,
        client_edit_id: clientEditId ?? crypto.randomUUID(),
        base_seq: baseSeq,
        acknowledged_seqs: acknowledgedSeqs ?? [],
      }),
    })
  );
}

// ── Lifecycle transitions ─────────────────────────────────────────────────────

/** POST one lifecycle transition endpoint. The study id alone identifies (and authorizes) it. */
async function postTransition<T extends StudyResponse = StudyResponse>(
  studyId: string,
  action: string
): Promise<T> {
  return json(await apiRequest(`/studies/${studyId}/${action}`, { method: "POST" }));
}

/** DRAFT → LIVE: publish revision 1 and open the public link for interviews. Also the superadmin
 *  approve action for a requested study (REQUESTED → LIVE). Full-access only. */
export async function launchStudy(studyId: string): Promise<StudyTransitionResult> {
  return postTransition<StudyTransitionResult>(studyId, "launch");
}

/** DRAFT → REQUESTED: a preview org's self-serve launch request (pending superadmin approval).
 *  Stays editable; not collecting. Emails the platform admins. */
export async function requestLaunchStudy(studyId: string): Promise<StudyResponse> {
  return postTransition(studyId, "request-launch");
}

/** REQUESTED → DRAFT: cancel a pending launch request. */
export async function withdrawLaunchStudy(studyId: string): Promise<StudyResponse> {
  return postTransition(studyId, "withdraw-launch");
}

/** LIVE → COMPLETED: stop new starts, enter Results, then finalize in the background. */
export async function closeStudy(studyId: string): Promise<StudyResponse> {
  return postTransition(studyId, "close");
}

/** Backward-compatible completion alias; repeating it retries a failed finalization. */
export async function completeStudy(studyId: string): Promise<StudyResponse> {
  return postTransition(studyId, "complete");
}

/** COMPLETED → LIVE: resume collection without changing the active scope revision. */
export async function reopenStudy(studyId: string): Promise<StudyTransitionResult> {
  return postTransition<StudyTransitionResult>(studyId, "reopen");
}

// ── Results: completed interviews (interviews domain) ─────────────────────────

export interface Highlight {
  text: string;
  turn_index: number | null;
  proposition: string | null;
}

/** Structured "who is this respondent" — the filterable counterpart to `description`. */
export interface RespondentProfile {
  role: string | null;
  company: string | null;
}

export type ProfileField = "role" | "company" | "country";
export type ProfileValueSource = "generated" | "human";
export type SessionProfilePatch = Partial<Record<ProfileField, string | null>>;

/** Concise, user-visible audit explanation for one extracted field (not model chain-of-thought). */
export interface FieldRationale {
  rationale: string;
  /** Participant transcript turns that directly support the value. */
  evidence_turns: number[];
  /** Model that produced the value/rationale; null for staff edits and legacy values. */
  model: string | null;
}

/** A quantitative claim the respondent cited (pricing, churn, market size, …). */
export interface DataPoint {
  label: string;
  value: string;
  context: string | null;
  turn_index?: number | null;
}

/** A company, product, or tool the respondent named (entity counterpart to themes). */
export interface Mention {
  name: string;
  turn_index?: number | null;
  valence?: string | null;
  /** "company" | "product" | "tool" | "other". */
  kind: string;
  context: string | null;
  /** Canonical official homepage/product page; absent on legacy extractions. */
  url?: string | null;
}

/** One ElevenLabs-scored user turn — the per-turn point behind the sentiment curve. */
export interface SentimentPoint {
  /** Transcript turn anchor for opening the scored message. */
  turn_index: number | null;
  /** time_in_call_secs of the turn (x-axis). */
  t: number | null;
  /** user_sentiment_score, roughly [-1, 1]. */
  sentiment: number | null;
  /** user_frustration_score, roughly [0, 1]. */
  frustration: number | null;
}

/** LATO's quality verdict for an interview: strong/adequate qualify it, insufficient does not. */
export type QualityVerdict = "strong" | "adequate" | "insufficient";

export interface InterviewAnalysis {
  /** ElevenLabs evaluation criteria. Only the qualification fallback for interviews extracted
   *  before `quality` existed reads them; the UI no longer shows them. */
  eval_flags: Record<string, string>;
  eval_rationales: Record<string, string>;
  /** LATO's quality verdict; its explanation is `field_rationales.quality`. Absent until the
   *  interview is extracted (or backfilled) with the quality pass. */
  quality?: QualityVerdict | null;
  /** Overall call outcome: "success" | "failure" | "unknown". */
  call_successful: string | null;
  /** Detected call language (ISO code, e.g. "en"). Native, set at ingest. */
  language: string | null;
  /** Our extraction pass (null/empty until it runs, or absent on rows predating a field): */
  /** Concise synthesis of this interview's most important study-relevant insight. */
  key_takeaway: string | null;
  profile: RespondentProfile | null;
  /** Provenance for canonical identity fields; absent on legacy values. */
  profile_sources?: Partial<Record<ProfileField, ProfileValueSource>>;
  /** Primary audience-segment id from the study's `audience_segments` ("other" = no fit). Absent
   *  on rows predating the field; an id no longer in the study's list buckets as Other/Unclear. */
  audience_segment?: string | null;
  /** Whether the current classification came from extraction or a staff correction. */
  audience_segment_source?: ProfileValueSource | null;
  themes: string[];
  /** ElevenLabs' native sentiment when the call has it, else LATO's extraction pass. */
  sentiment: string | null;
  sentiment_score: number | null;
  frustration_score: number | null;
  sentiment_source?: "elevenlabs" | "lato" | null;
  /** Per-turn sentiment/frustration over the call — the curve behind the label. */
  sentiment_timeline: SentimentPoint[];
  highlights: Highlight[];
  data_points: DataPoint[];
  /** Companies / products / tools the respondent named. */
  mentions: Mention[];
  /** Focused extraction field (including `answer:{objective_id}`) → audit explanation. */
  field_rationales?: Record<string, FieldRationale>;
  /** Per-objective high-signal details such as numbers, named competitors/tools, and outcomes. */
  objective_data_points?: Record<string, string[]>;
  /** Per-objective evidence: objective_id → verified verbatim participant quotes backing that
   *  objective's answer (from `structured_answers`). Only objectives with surviving quotes appear. */
  objective_evidence?: Record<string, Highlight[]>;
  /** Fields whose extraction failed on the last pass (objective fields keyed "answer:{id}") → the
   *  error. A degraded interview reaches `done` with these gaps recorded; the results pill reads
   *  "Partial" while any remain. Absent on rows predating this field. */
  field_errors?: Record<string, string>;
}

/** Resolved qualification: the automatic quality-verdict outcome, or an admin's manual override.
 *  The org only ever receives `qualified` rows; LATO staff also see review, pending and disqualified. */
export type Qualification = "qualified" | "review" | "pending" | "disqualified";

export interface SessionResult {
  id: string;
  kind: string;
  status: string;
  /** Effective qualification (auto verdict or manual override). */
  qualification: Qualification;
  /** The raw manual override, if any (so the admin controls reflect the current state). */
  qualification_override: "qualified" | "review" | "disqualified" | null;
  /** The verdict with no override applied (acting admin only); what clearing the override yields. */
  automatic_qualification?: Qualification | null;
  /** Why this interview was excluded. Admin-only, like the raw override; the org never receives a
   *  disqualified row. */
  qualification_reason?: string | null;
  label: string;
  /** External Participant ID from the recruitment link (None for anonymous public sessions). */
  external_participant_id: string | null;
  summary: string | null;
  structured_answers: Record<string, string | null> | null;
  analysis: InterviewAnalysis | null;
  audio_duration_seconds: number | null;
  /** Number of transcript turns (participant + agent). */
  message_count: number;
  /** How the call ended (free text, may be empty). */
  ended_reason: string | null;
  /** Provider conversation id, for matching a session to its call record. */
  conversation_id: string | null;
  recording_url: string | null;
  /** When the interview began (drives the results trend charts) and when it finished. */
  started_at: string | null;
  completed_at: string | null;
  /** Admin-only labels (LATO staff annotations). Empty unless the viewer is LATO staff —
   *  the backend withholds them from the org's own view. */
  admin_labels: string[];
  /** Admin-only free-text note. Null unless the viewer is LATO staff (withheld from the org). */
  admin_note: string | null;
  /** Participant post-call feedback. Admin-only; null in org and public projections. */
  feedback_rating: number | null;
  feedback_text: string | null;
  /** Participant-facing biography plus canonical country. Country is extracted automatically and
   *  can be corrected by a platform admin through Profile. */
  biography: string | null;
  country: string | null;
  /** The biography the client reads under Experts, from the published expert this interview is
   *  attributed to. Nothing else of the expert comes with it. */
  expert_biography?: string | null;
  /** Staff-only attribution: the link it came through and the Experts-ledger record it is
   *  attributed to. Null/absent for members. */
  distribution_id?: string | null;
  study_participant_id?: string | null;
  /** When the interview actually happened, if known (e.g. supplied with an upload). For uploads,
   *  `started_at`/`completed_at` are the upload time. */
  interviewed_on?: string | null;
}

export interface TranscriptTurn {
  role?: string;
  message?: string;
  text?: string;
  /** Start offset into the call (seconds), stamped by ElevenLabs. */
  time_in_call_secs?: number | null;
}

/** Admin-only metadata for the configured interview link that produced a session. */
export interface SessionLinkInfo {
  id: string;
  label: string;
  url: string;
  recruitment_mode: "public_link" | "respondent" | "prolific" | "custom";
  participant_id_source: "none" | "manual" | "url_query";
  participant_id_param: string | null;
  apply_screener: boolean;
  enabled: boolean;
}

export interface SessionScreenerSelection {
  id: string;
  label: string;
}

/** Admin-only readable projection of one stored screener answer. */
export interface SessionScreenerAnswer {
  question_id: string;
  question: string;
  selections: SessionScreenerSelection[];
}

/** One target-language rendering of a transcript, for display only.
 *
 *  `turns` maps a transcript turn's index (as a string) to its translated message, and is populated
 *  only once `status` is "done". Keying by index is what keeps everything else still: quote anchors,
 *  evidence turns, sentiment points and the player's markers all index the same array, so switching
 *  views swaps text per turn and moves nothing else. Quotes cited elsewhere in the UI stay in the
 *  spoken language — they are evidence matched against the stored transcript, not display copy. */
export interface TranscriptTranslation {
  status: "pending" | "running" | "done" | "failed";
  /** The detected or inferred language of the call, e.g. "de". */
  source_language: string | null;
  target_language: string;
  turns: Record<string, string>;
  /** Safe server-authored failure text; present only for a failed durable artifact. */
  error?: string | null;
}

export interface SpokenLanguages {
  primary_language: string | null;
  languages: string[];
}

export interface SessionDetail extends SessionResult {
  transcript: TranscriptTurn[] | null;
  /** Legacy English compatibility projection. Null when absent or when the stored rendering no
   *  longer matches the transcript (the backend withholds stale content rather than misalign it). */
  transcript_translation: TranscriptTranslation | null;
  /** Fresh target-language renderings keyed by language. Optional during the compatibility rollout. */
  transcript_translations?: Record<string, TranscriptTranslation>;
  /** Best current source language for the canonical original transcript. */
  transcript_language?: string | null;
  transcript_language_source?: "manual" | "provider" | "participant" | "inferred" | "unknown";
  /** Languages actually spoken in the current transcript; null until extraction detects them. */
  spoken_languages?: SpokenLanguages | null;
  transcript_translation_targets?: Record<string, string>;
  /** Null outside a LATO-staff response (and for legacy/deleted links). */
  link: SessionLinkInfo | null;
  /** Empty outside a LATO-staff response or when this interview had no screener. */
  screener_answers: SessionScreenerAnswer[];
  /** Stable authed URL for the source document an imported transcript came from. Null for a
   *  voice interview or an uploaded recording. */
  import_document_url?: string | null;
  /** Why an import failed, in terms the uploader can act on. Bounded reader-safe copy. */
  import_error?: string | null;
}

/** Acting-admin bulk pass over blank transcript-derived analysis fields. A successful regeneration
 * may remain empty when the transcript contains no supporting evidence. */
export interface FillMissingAnalysisResult {
  eligible_sessions: number;
  targeted_sessions: number;
  attempted_fields: number;
  regenerated_fields: number;
  filled_fields: number;
  remaining_empty_fields: number;
  failed_fields: number;
}

export type FillMissingAnalysisStatus = "pending" | "running" | "done" | "failed";

/** Pollable state for the durable LATO-staff bulk pass. */
export interface FillMissingAnalysisRun extends FillMissingAnalysisResult {
  status: FillMissingAnalysisStatus;
  processed_fields: number;
  error: string | null;
}

export async function getStudySessions(
  studyId: string,
  scopeRevisionId?: string
): Promise<SessionResult[]> {
  const query = scopeRevisionId ? `?scope_revision_id=${encodeURIComponent(scopeRevisionId)}` : "";
  return json(await apiRequest(`/interviews/studies/${studyId}/sessions${query}`));
}

/** The slim projection interview numbering derives from — identity, qualification, chronology.
 * Report citation markers resolve their numbers from this instead of blocking on the full
 * sessions payload (analysis blobs, answers, recording URLs). */
export interface SessionNumberingEntry {
  id: string;
  qualification: Qualification;
  started_at: string | null;
  completed_at: string | null;
}

export async function getStudySessionNumbering(
  studyId: string,
  scopeRevisionId?: string
): Promise<SessionNumberingEntry[]> {
  const query = scopeRevisionId ? `?scope_revision_id=${encodeURIComponent(scopeRevisionId)}` : "";
  return json(await apiRequest(`/interviews/studies/${studyId}/sessions/numbering${query}`));
}

/** Upload an external interview recording. The 202 response is the new `transcribing` session;
 * durable transcription then hands it to the same extraction workflow as a normal voice call. */
/** Optional upload attribution. `studyParticipantId` is staff-only (the backend refuses it from
 *  members); `interviewedOn` is the actual call date (YYYY-MM-DD) when known. */
export interface UploadAttribution {
  studyParticipantId?: string | null;
  interviewedOn?: string | null;
}

function uploadForm(file: File, attribution: UploadAttribution = {}): FormData {
  const body = new FormData();
  body.append("file", file);
  if (attribution.studyParticipantId) {
    body.append("study_participant_id", attribution.studyParticipantId);
  }
  if (attribution.interviewedOn) body.append("interviewed_on", attribution.interviewedOn);
  return body;
}

export async function uploadStudyRecording(
  studyId: string,
  file: File,
  attribution?: UploadAttribution
): Promise<SessionDetail> {
  const body = uploadForm(file, attribution);
  return json(
    await apiRequest(`/interviews/studies/${studyId}/recordings`, {
      method: "POST",
      body,
      // Large recordings can legitimately take longer than the normal REST timeout to reach S3.
      signal: AbortSignal.timeout(15 * 60_000),
    })
  );
}

/** Import an already-transcribed interview document. The 202 response is the new `importing`
 * session; the durable parse then writes the transcript and hands it to extraction. */
export async function importStudyTranscript(
  studyId: string,
  file: File,
  attribution?: UploadAttribution
): Promise<SessionDetail> {
  const body = uploadForm(file, attribution);
  return json(
    await apiRequest(`/interviews/studies/${studyId}/transcripts`, { method: "POST", body })
  );
}

/** Delete a failed import — the interview row and the stored document.
 *
 * `apiRequest` resolves with the Response whatever the status, so a caller that ignores it
 * reports success on a refusal — which is how a rejected discard once produced an "Import
 * discarded" toast beside a row that was still there. */
export async function discardTranscriptImport(sessionId: string): Promise<void> {
  const response = await apiRequest(`/interviews/sessions/${sessionId}/transcript/import`, {
    method: "DELETE",
  });
  // A 204 has no body to parse, so this cannot simply `return json(...)` — but `json` still owns
  // the error path, and skipping it is how a refused discard once reported success.
  if (!response.ok) await json(response);
}

/** Admin (LATO staff only): enqueue a retry of blank transcript-derived fields. */
export async function fillMissingStudyAnalysis(studyId: string): Promise<FillMissingAnalysisRun> {
  return json(
    await apiRequest(`/interviews/studies/${studyId}/analysis/fill-missing`, { method: "POST" })
  );
}

/** Admin (LATO staff only): read the latest bulk pass so the UI can poll durable progress. */
export async function getFillMissingStudyAnalysis(
  studyId: string
): Promise<FillMissingAnalysisRun | null> {
  return json(await apiRequest(`/interviews/studies/${studyId}/analysis/fill-missing`));
}

export async function getSession(sessionId: string): Promise<SessionDetail> {
  return json(await apiRequest(`/interviews/sessions/${sessionId}`));
}

/** Generate or retry one target-language transcript rendering. */
export async function requestTranscriptTranslation(
  sessionId: string,
  targetLanguage: string
): Promise<SessionDetail> {
  return json(
    await apiRequest(
      `/interviews/sessions/${sessionId}/translations/${encodeURIComponent(targetLanguage)}`,
      { method: "POST" }
    )
  );
}

/** Correct the transcript's spoken language, or clear the correction back to automatic. */
export async function updateTranscriptLanguage(
  sessionId: string,
  language: string | null
): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/transcript-language`, {
      method: "PATCH",
      body: JSON.stringify({ language }),
    })
  );
}

/** Check actual spoken languages once per transcript; retry failures only when requested. */
/** Re-run a single field's extraction over the stored transcript, returning the updated session.
 *  Drives the per-field "Regenerate" affordance in the detail sheet. `field` is an analysis key
 *  ("summary", "sentiment", …) or an objective answer ("answer:{id}"). */
export async function reextractField(sessionId: string, field: string): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/reextract`, {
      method: "POST",
      body: JSON.stringify({ field }),
    })
  );
}

/** Force a whole-session re-extraction (the "Retry extraction" affordance for a failed/wedged
 *  session). Flips the session back to `extracting` and enqueues a fresh durable run; the returned
 *  session reflects the `extracting` state. */
export async function reextractAll(sessionId: string): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/reextract-all`, {
      method: "POST",
    })
  );
}

/** Admin (LATO staff only): enqueue a fresh server-side recording fetch from ElevenLabs. The response
 *  is immediate; recording_url remains null until the background capture reaches object storage. */
export async function retrySessionRecording(sessionId: string): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/recording/retry`, {
      method: "POST",
    })
  );
}

/** Manually qualify/disqualify an interview, overriding the automatic quality verdict. `null`
 *  reverts to the automatic verdict. An org member may only send `disqualified` with a reason; the
 *  backend 403s anything else unless the caller is LATO staff. */
export async function setSessionQualification(
  sessionId: string,
  qualification: "qualified" | "review" | "disqualified" | null,
  reason?: string | null
): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/qualification`, {
      method: "POST",
      body: JSON.stringify({ qualification, reason: reason ?? null }),
    })
  );
}

/** Admin (LATO staff only): replace an interview's admin-only labels + note. Labels must be known
 *  AdminLabel ids (see `ADMIN_LABELS`); an empty note clears it. The backend 403s anyone but LATO staff. */
export async function setSessionAnnotation(
  sessionId: string,
  labels: string[],
  note: string | null
): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/annotation`, {
      method: "POST",
      body: JSON.stringify({ labels, note }),
    })
  );
}

/** Rename an interview in the org's own list. Empty reverts to the derived label. */
export async function setSessionName(
  sessionId: string,
  name: string | null
): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/name`, {
      method: "POST",
      body: JSON.stringify({ name }),
    })
  );
}

/** Admin (LATO staff only): set participant-facing biography text. */
export async function setSessionBiography(
  sessionId: string,
  biography: string | null
): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/biography`, {
      method: "POST",
      body: JSON.stringify({ biography }),
    })
  );
}

/** Admin (LATO staff only): manually assign this respondent to a current audience segment. */
export async function setSessionAudienceSegment(
  sessionId: string,
  segmentId: string
): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/audience-segment`, {
      method: "POST",
      body: JSON.stringify({ segment_id: segmentId }),
    })
  );
}

/** Admin (LATO staff only): correct canonical respondent identity and country. */
export async function setSessionProfile(
  sessionId: string,
  patch: SessionProfilePatch
): Promise<SessionDetail> {
  return json(
    await apiRequest(`/interviews/sessions/${sessionId}/profile`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    })
  );
}

// ── Study-level report (analysis domain) ──────────────────────────────────────

export type ReportStatus = Api.ReportStatus;

/**
 * Report generation persists `running` before its Research workflow is queued. `pending` means the
 * report has never been generated or was invalidated by a scope/lifecycle change; it is idle and
 * must offer Generate rather than Stop. Research-attempt `pending` has different queued semantics.
 */
export function isReportGenerationRunning(status: ReportStatus | null | undefined): boolean {
  return status === "running";
}

export interface StudyQuote {
  text: string;
  session_id: string | null;
  /** Transcript turn the quote was anchored to, for opening the conversation seeked to that moment. */
  turn_index?: number | null;
}

export interface ParticipationStats {
  completed: number;
  avg_duration_seconds: number | null;
  objective_coverage: Record<string, number>;
}

export interface StudySummary {
  headline_themes: string[];
  top_quotes: StudyQuote[];
  stats: ParticipationStats;
}

/** One deduped secondary (web) source — the Sources-tab bibliography; `id` is the source URL. */
/** A verbatim, page-anchored passage a secondary source backs — the exact substring we relied on,
 *  plus the `#:~:text=start,end` endpoints for deep-linking to it on the live page. `deeplinkable`
 *  is false for PDF/paywalled sources where a browser text fragment won't land. */
export interface SourceReference {
  quote: string;
  text_start: string;
  text_end: string | null;
  deeplinkable: boolean;
  /** The evidence atom this passage came from — the Details sheet highlights the clicked citation's
   *  passage by this identity. Null on references from reports predating the atom migration. */
  evidence_id?: string | null;
  /** Immutable viewport captured at this exact cited passage. */
  screenshot_id?: string | null;
}

/** A web source's credibility grade, A best → D worst (see backend source_policy). Distinct from
 *  `ReportCitation.kind` (primary/secondary = interview vs web), so the two axes never read alike. */
export type SourceTier = "A" | "B" | "C" | "D";

export interface ReportSource {
  id: string;
  url: string;
  title: string;
  snippet: string;
  /** Blended credibility grade (see backend source_policy); null on legacy rows. */
  tier?: SourceTier | null;
  /** Whole-page/source preview captured during the fresh Firecrawl acquisition. */
  screenshot_id?: string | null;
  /** Exact page-anchored passages this source backs (the Details tab + deep links). */
  references?: SourceReference[];
}

/** A resolved citation a section's prose points at. Primary → the interview's cited-passages
 *  sheet (whose passages open conversation+recording at the turn); secondary → the `ReportSource`
 *  (`source_id` === its URL) in the Sources tab, deep-linked to the exact page passage.
 *
 *  `evidence_id` is the atom this citation resolves to — its stable identity, what the Details sheet
 *  highlights by (no quote string-matching). Null on reports persisted before the atom migration;
 *  the sheet then falls back to matching `quote_text`. */
export interface ReportCitation {
  kind: "primary" | "secondary";
  evidence_id?: string | null;
  session_id: string | null;
  turn_index: number | null;
  quote_text: string | null;
  /** The turn ran on past `quote_text`. Structural rather than an ellipsis inside the string,
   *  because every character between the quotation marks has to be one the respondent said. */
  truncated?: boolean;
  source_id: string | null;
  /** secondary only — the atom's page-anchored span, carried inline so the click deep-links without
   *  depending on `ReportSource.references`. Absent on legacy citations. */
  text_start?: string | null;
  text_end?: string | null;
  deeplinkable?: boolean;
}

export type ChartType =
  | "bar"
  | "grouped_bar"
  | "distribution"
  | "quadrant"
  | "matrix"
  | "valence"
  | "harvey_matrix"
  | "stat"
  | "range";

/** One datum in an exhibit. Which fields matter depends on the chart type (see backend ChartPoint):
 *  bar/distribution use label+value; grouped_bar/matrix/valence/harvey_matrix add `group`; quadrant
 *  uses x+y; stat uses value+unit; range uses value(median)+low(min)+high(max)+unit. `provenance`
 *  are the citations behind the point (click-through to the quote), like a prose citation. */
export interface ChartPoint {
  label: string;
  value: number;
  group: string | null;
  x: number | null;
  y: number | null;
  unit: string | null;
  low: number | null;
  high: number | null;
  provenance: ReportCitation[];
}

/** A typed exhibit rendered client-side from data (never a model image). `title` is the action title
 *  (the insight); `takeaway` the one-line "so what". */
export interface ReportChart {
  chart_type: ChartType;
  title: string;
  takeaway: string;
  x_axis: string | null;
  y_axis: string | null;
  points: ChartPoint[];
  section_heading: string | null;
  /** What the exhibit is about (composition / positioning / …); server-side placement/budget field,
   *  not read by the renderer. Optional for parity with the backend schema. */
  kind?: string | null;
  /** Who the bars are counted over — the base, how many held no view, whether bars may sum past the
   *  base, and the column's coding reliability. Rendered under the figure: a count without its
   *  denominator is the failure this whole layer exists to remove. */
  base_note?: string;
  /** Segment filters, the first being the whole corpus. Each view carries its own points AND its
   *  own base note, because they are computed together server-side — never recompute a base here. */
  views?: ChartView[];
  /** Whether the segments actually answer this question differently, so the reader knows which of
   *  twenty exhibits is worth toggling. */
  segments_differ?: boolean;
}

/** One cut of an exhibit: a segment's own points and its own denominator. */
export interface ChartView {
  /** "" is the whole corpus; anything else names an audience segment. */
  segment: string;
  label: string;
  base: number;
  points: ChartPoint[];
  base_note: string;
}

/** The Exhibits tab payload. `status` is the NEWEST attempt's, which is not always the attempt that
 *  produced `charts` — a failed or running regeneration sits in front of the artifact a reader is
 *  still looking at, so the tab can say "showing the last good exhibits, a new run failed". */
export interface ExhibitsResponse {
  status: string;
  charts: ReportChart[];
  base: number;
  generated_at: string | null;
  attempt_id: string | null;
  error_code: string | null;
  is_stale: boolean;
}

/** Whether this study has exhibits worth offering a tab for.
 *
 *  Guards the field, not just the response. `exhibits?.charts.length` stops the optional chain one
 *  level too early, so any payload without a `charts` array threw where it was read — and because
 *  that read sits in the study page's tab computation, the throw took down the whole page rather
 *  than the tab. One unexpected response shape, and a reader gets "Something went wrong". */
export function hasExhibits(data: ExhibitsResponse | undefined): boolean {
  return (data?.charts?.length ?? 0) > 0;
}

export async function getStudyExhibits(studyId: string): Promise<ExhibitsResponse> {
  return json(await apiRequest(`/studies/${studyId}/exhibits`));
}

export async function generateStudyExhibits(studyId: string): Promise<ExhibitsResponse> {
  return json(await apiRequest(`/studies/${studyId}/exhibits/generate`, { method: "POST" }));
}

export async function cancelStudyExhibits(studyId: string): Promise<ExhibitsResponse> {
  return json(await apiRequest(`/studies/${studyId}/exhibits/cancel`, { method: "POST" }));
}

/** One diligence objective's verdict — the scorecard close. Verdict is computed from respondent
 *  valence counts server-side; `rationale` is the one-line narration; `evidence` links respondents. */
export interface ThesisVerdict {
  objective_id: string;
  goal: string;
  verdict: "supported" | "mixed" | "refuted" | "insufficient";
  supporting_count: number;
  contradicting_count: number;
  response_count: number;
  rationale: string;
  evidence: ReportCitation[];
}

/** The deterministic study-level fact base behind the exhibits (mirrors backend StudyStatistics).
 *  The UI renders the derived ReportCharts rather than these tables directly, but the payload carries
 *  them for standalone/derived rendering. */
export interface StatBucket {
  label: string;
  count: number;
  session_ids: string[];
}
export interface CompositionDimension {
  dimension: string;
  buckets: StatBucket[];
}
export interface SampleComposition {
  total: number;
  dimensions: CompositionDimension[];
}
export interface StudyStatistics {
  sample_composition: SampleComposition;
  theme_prevalence: {
    objective_id: string;
    response_count: number;
    themes: StatBucket[];
    // Per-theme evaluative polarity, keyed by theme label (only themes with a known valence).
    theme_valence: Record<string, Valence>;
  }[];
  valence_distribution: {
    objective_id: string;
    supports: number;
    contradicts: number;
    mixed: number;
    neutral: number;
  }[];
  metric_distributions: {
    metric_type: string;
    n: number;
    median: number | null;
    minimum: number | null;
    maximum: number | null;
    points: {
      value: number;
      unit: string | null;
      label: string | null;
      session_id: string | null;
    }[];
  }[];
  competitor_frequency: {
    name: string;
    count: number;
    positive: number;
    negative: number;
    neutral: number;
    mixed: number;
    session_ids: string[];
  }[];
  segment_crosstabs: {
    objective_id: string;
    segment_dimension: string;
    segments: string[];
    rows: { theme: string; counts: Record<string, number> }[];
  }[];
}

/** One report section: markdown `body` with `[[p:n]]`/`[[s:n]]` tokens resolved against `citations`
 *  (the n-th primary / n-th secondary), plus any exhibits attached to this section. */
export interface ReportSection {
  heading: string;
  body: string;
  objective_ids: string[];
  citations: ReportCitation[];
  charts: ReportChart[];
}

export interface StudyReportContent {
  /** Absent on rows stored before versioning; the discriminator against v2 (`isReportV2`). */
  version?: 1;
  exec_summary: string;
  sections: ReportSection[];
  recommendations: string[];
  sources: ReportSource[];
  statistics: StudyStatistics | null;
  charts: ReportChart[];
  thesis_scorecard: ThesisVerdict[];
}

// ── v2 report content: thesis-led, block-based (mirrors backend schemas) ─────────

/** A thesis' code-owned verdict, computed from its per-respondent stances — conviction labels,
 *  never percentages at expert-call N. */
export type ThesisVerdictV2 =
  | "strongly_supported"
  | "directional_support"
  | "contested"
  | "contradicted"
  | "untested";

export type StanceLabel = "supports" | "contradicts" | "mixed" | "not_addressed";

/** One respondent's evidence for/against one thesis — a cell of the evidence matrix. `quote` is the
 *  best anchored quote backing the stance (click-through to the transcript turn). */
export interface ThesisStance {
  session_id: string;
  stance: StanceLabel;
  quote: StudyQuote | null;
  note: string;
}

/** One falsifiable investment thesis generated at report time (never a restated objective). */
export interface ReportThesis {
  id: string;
  statement: string;
  origin_objective_ids: string[];
  verdict: ThesisVerdictV2;
  stances: ThesisStance[];
}

/** Markdown prose carrying `[[p:n]]`/`[[s:n]]` tokens resolved against `citations` — the v1 section
 *  body contract, one block instead of the whole section. */
export interface ProseBlock {
  type: "prose";
  md: string;
  citations: ReportCitation[];
}

export interface QuoteCardBlock {
  type: "quote_card";
  quote: StudyQuote;
  attribution: string;
}

/** Placement marker: the matrix's data lives on the document's top-level `theses`. */
export interface EvidenceMatrixBlock {
  type: "evidence_matrix";
}

/** One quantitative figure WITH its respondent — never aggregated into a distribution. */
export interface AttributedValue {
  label: string;
  value: number;
  unit: string | null;
  session_id: string | null;
}

export interface DataPointsBlock {
  type: "datapoints";
  title: string;
  values: AttributedValue[];
}

/** The competitive-positioning 2x2 — same point shape as the v1 quadrant chart. */
export interface PositioningBlock {
  type: "positioning";
  title: string;
  takeaway: string;
  x_axis: string | null;
  y_axis: string | null;
  points: ChartPoint[];
}

export interface TableBlock {
  type: "table";
  title: string;
  columns: string[];
  rows: string[][];
  /** Cell `[[p:n]]`/`[[s:n]]` tokens resolve against this block-scoped list (same contract as
   *  ProseBlock). Optional: absent on reports written before table citations existed. */
  citations?: ReportCitation[];
}

export type ReportBlock =
  | ProseBlock
  | QuoteCardBlock
  | EvidenceMatrixBlock
  | DataPointsBlock
  | PositioningBlock
  | TableBlock;

export interface ReportSectionV2 {
  heading: string;
  objective_ids: string[];
  blocks: ReportBlock[];
}

/** One objective's deterministic evidence coverage — the honest "what we actually heard" ledger. */
export interface CoverageRow {
  objective_id: string;
  goal: string;
  respondents_answered: number;
  evidence_count: number;
}

export interface CoverageReport {
  rows: CoverageRow[];
  gaps: string[];
  next_probes: string[];
  untested_thesis_ids: string[];
}

/** The v2 report document: thesis-led, block-based. `thesis_lead` is the deterministic net-verdict
 *  line rendered as the thesis board's lead (its own field, never merged into `exec_summary`). */
export interface StudyReportContentV2 {
  version: 2;
  exec_summary: string;
  /** The exec summary's citation scope — its `[[p:n]]`/`[[s:n]]` tokens resolve here. Optional:
   *  absent on reports written before the exec summary became a cited surface. */
  exec_citations?: ReportCitation[];
  thesis_lead: string;
  theses: ReportThesis[];
  sections: ReportSectionV2[];
  coverage: CoverageReport;
  recommendations: string[];
  sources: ReportSource[];
  statistics: StudyStatistics | null;
}

// ── v3 reader report: authored-key-question spine ────────────────────────────────

export interface CompetitiveLandscapeCell {
  value: string;
  state: "grounded" | "unknown" | "not_applicable";
  citations: ReportCitation[];
  claim_id: string | null;
}

export interface CompetitiveLandscapeRow {
  entity_id: string;
  company: string;
  is_subject: boolean;
  identity_resolution: "resolved" | "ambiguous" | "unresolved";
  cells: Record<string, CompetitiveLandscapeCell>;
}

export interface CompetitiveLandscapeBlock {
  type: "competitive_landscape";
  title: string;
  columns: string[];
  rows: CompetitiveLandscapeRow[];
}

export interface CompetitivePositionPoint {
  entity_id: string;
  label: string;
  x: number;
  y: number;
  x_provenance: ReportCitation[];
  y_provenance: ReportCitation[];
  is_subject: boolean;
}

export interface CompetitivePositioningBlock {
  type: "competitive_positioning";
  title: string;
  takeaway: string;
  x_axis: string;
  y_axis: string;
  points: CompetitivePositionPoint[];
}

export type ReportBlockV3 =
  | ProseBlock
  | QuoteCardBlock
  | DataPointsBlock
  | PositioningBlock
  | TableBlock
  | CompetitiveLandscapeBlock
  | CompetitivePositioningBlock;

export interface ReportSectionV3 {
  key_question_id: string;
  question: string;
  headline: string;
  blocks: ReportBlockV3[];
  implication: string;
  open_items: string[];
  supporting_claim_ids: string[];
  challenging_claim_ids: string[];
}

export interface StudyReportContentV3 {
  version: 3;
  spine: "key_questions" | "objectives";
  as_of: string;
  executive_summary: string;
  executive_citations: ReportCitation[];
  sections: ReportSectionV3[];
  opportunities: string[];
  risks: string[];
  open_items: string[];
  sources: ReportSource[];
  statistics: StudyStatistics | null;
}

// ── v4 report: narrative storyline spine ─────────────────────────────────────────

/** A key question's code-derived scorecard verdict (never model judgment). */
export type KeyQuestionVerdict = "answered" | "contested" | "insufficient";

export type ScorecardConfidence = "high" | "medium" | "low";

/** One narrative section: an assertion `title` (the finding, not the question) over cited blocks
 *  drawing on claims from any key question. `section_id` ("s1".."s7") anchors scorecard links. */
export interface ReportSectionV4 {
  section_id: string;
  title: string;
  governing_thought: string;
  blocks: ReportBlockV3[];
  key_question_ids: string[];
  supporting_claim_ids: string[];
  challenging_claim_ids: string[];
}

/** One authored key question's verdict row — the coverage guarantee the spine no longer provides.
 *  `question` and `headline` are verbatim canonical strings. */
export interface KeyQuestionScorecardRow {
  key_question_id: string;
  question: string;
  headline: string;
  verdict: KeyQuestionVerdict;
  confidence: ScorecardConfidence;
  section_ids: string[];
  open_items: string[];
  citations: ReportCitation[];
}

/** An accepted claim the architect deliberately left out of the narrative — internal/operator
 *  ledger, never rendered. */
export interface DroppedClaim {
  claim_id: string;
  reason: string;
}

/** The v4 narrative, finding-led report contract: assertion-titled sections over the same v3 block
 *  union, with key-question coverage moved off the spine into the code-built scorecard. */
export interface StudyReportContentV4 {
  version: 4;
  as_of: string;
  executive_summary: string;
  executive_citations: ReportCitation[];
  sections: ReportSectionV4[];
  key_question_scorecard: KeyQuestionScorecardRow[];
  dropped_claims: DroppedClaim[];
  opportunities: string[];
  risks: string[];
  open_items: string[];
  sources: ReportSource[];
  statistics: StudyStatistics | null;
}

// ── v5 report content: one immutable research state, storyline spine ─────────
//
// The v5 document carries only what plan §5.4 allows a reader to see: an executive summary,
// storyline sections over anchored exhibits, open questions, and the sources actually cited.
// The competitive chart, the key-question scorecard, and the cross-question roll-up are gone
// (§11.1), as is every internal identifier — sections carry no finding or proof-point ids.
//
// Citation rendering is baked in server-side: `md` carries `[[m:k]]` marker tokens that index
// `markers`, and each marker points into the block's own `citations`. The renderer never
// re-derives grouping.

/** One visible citation marker and the resolved citations it stands for (§6 rules 7-8). */
export interface CitationMarker {
  kind: "primary" | "secondary";
  /** Primary: the count of distinct interviews. Secondary: an ordinal or range ("2-4"). */
  label: string;
  citation_indexes: number[];
}

export interface ProseBlockV5 {
  type: "prose";
  /** Insight-led title for a child key question; absent on historical reports. */
  title?: string;
  /** Code-owned terminal evidence state; the copy reports what the study could not establish. */
  evidence_gap?: boolean;
  md: string;
  citations: ReportCitation[];
  markers: CitationMarker[];
}

export interface TableBlockV5 {
  type: "table";
  title: string;
  columns: string[];
  rows: string[][];
  citations: ReportCitation[];
  markers: CitationMarker[];
}

export type ReportBlockV5 = ProseBlockV5 | QuoteCardBlock | DataPointsBlock | TableBlockV5;

export interface ReportSectionV5 {
  section_id: string;
  title: string;
  governing_thought: string;
  /** Code-owned: a section carrying a critical proof point (§6). A model cannot downgrade it. */
  required: boolean;
  key_question_ids: string[];
  blocks: ReportBlockV5[];
  /** Methodological caveats rendered separately from evidence-bearing prose. */
  limitations?: string[];
}

export interface StudyReportContentV5 {
  version: 5;
  as_of: string;
  executive_summary: string;
  /** Plain-language study framing for readers who are new to the topic. */
  introduction?: string;
  executive_citations: ReportCitation[];
  sections: ReportSectionV5[];
  open_questions: string[];
  /** Code-owned methodological caveats presented once in the closing section. */
  limitations?: string[];
  /** Cited sources only — never an additional-source list (§11.1). */
  sources: ReportSource[];
  statistics: StudyStatistics | null;
}

export type AnyStudyReportContent =
  | StudyReportContent
  | StudyReportContentV2
  | StudyReportContentV3
  | StudyReportContentV4
  | StudyReportContentV5;

/** Narrow a stored report to v5 before evaluating older-version shapes. */
export function isReportV5(content: AnyStudyReportContent): content is StudyReportContentV5 {
  return content.version === 5;
}

/** Narrow a stored report to v4. */
export function isReportV4(content: AnyStudyReportContent): content is StudyReportContentV4 {
  return content.version === 4;
}

/** Narrow a stored report to v3. */
export function isReportV3(content: AnyStudyReportContent): content is StudyReportContentV3 {
  return content.version === 3;
}

/** Narrow a stored report to v2. Rows persisted before versioning carry no `version` and are v1. */
export function isReportV2(content: AnyStudyReportContent): content is StudyReportContentV2 {
  return content.version === 2;
}

export interface StudyReportResponse {
  study_id: string;
  status: ReportStatus;
  source_session_count: number;
  summary: StudySummary | null;
  report: AnyStudyReportContent | null;
  error: string | null;
  error_code: string | null;
  generated_at: string | null;
  /** Whether the released report's pinned inputs drifted from the current corpus (§8.2). */
  is_stale?: boolean;
  progress?: GenerationProgress | null;
}

/** The latest report, or null when one has never been generated (the backend returns 404). */
export async function getStudyReport(studyId: string): Promise<StudyReportResponse | null> {
  const resp = await apiRequest(`/studies/${studyId}/report`);
  if (resp.status === 404) return null;
  return json(resp);
}

export async function generateStudyReport(studyId: string): Promise<StudyReportResponse> {
  return json(await apiRequest(`/studies/${studyId}/report/generate`, { method: "POST" }));
}

export async function cancelStudyReport(studyId: string): Promise<StudyReportResponse> {
  return json(await apiRequest(`/studies/${studyId}/report/cancel`, { method: "POST" }));
}

// ── Responses tab: per-objective response groupings ──────────────────────────────

export type Valence = "positive" | "negative" | "mixed" | "neutral";

export interface ResponseGroup {
  label: string;
  summary: string;
  respondent_count: number;
  quotes: StudyQuote[];
  // How respondents in this theme feel about it; null on codebooks grouped before valence existed.
  valence: Valence | null;
}

/** Cohort dimensions for one respondent in the exact Responses snapshot. */
export interface ResponseGroupsRespondent {
  session_id: string;
  audience_segment_id: string | null;
  country: string | null;
}

export interface ObjectiveResponseGroups {
  objective_id: string;
  response_count: number;
  /** Exact respondents behind `response_count`, used for cohort projections. */
  respondent_session_ids: string[];
  groups: ResponseGroup[];
}

export interface ResponseGroupsContent {
  /** Exact source cohort; absent only in codebooks generated before projections existed. */
  respondents: ResponseGroupsRespondent[];
  objectives: ObjectiveResponseGroups[];
}

export interface ResponseGroupsResponse {
  study_id: string;
  status: ReportStatus;
  response_groups: ResponseGroupsContent | null;
  error: string | null;
  error_code: string | null;
  generated_at: string | null;
  source_session_count: number;
  is_stale: boolean;
  progress?: GenerationProgress | null;
}

/** The Responses artifact, or null when generation was never requested (the backend returns 404). */
export async function getResponseGroups(studyId: string): Promise<ResponseGroupsResponse | null> {
  const resp = await apiRequest(`/studies/${studyId}/report/response-groups`);
  if (resp.status === 404) return null;
  return json(resp);
}

export async function generateResponseGroups(studyId: string): Promise<ResponseGroupsResponse> {
  return json(
    await apiRequest(`/studies/${studyId}/report/response-groups/generate`, { method: "POST" })
  );
}

export async function cancelResponseGroups(studyId: string): Promise<ResponseGroupsResponse> {
  return json(
    await apiRequest(`/studies/${studyId}/report/response-groups/cancel`, { method: "POST" })
  );
}

// ── Hypotheses tab: key-question decision view over grounded interview evidence ─────────

export type OverviewEvidenceStance = "supports_view" | "challenges_view";
export type OverviewEvidencePattern = "not_assessable" | "convergent" | "contested";
export type OverviewEvidenceState = "open" | "emerging" | "converging" | "contested";
export type OverviewEvidenceStrength = "none" | "weak" | "moderate" | "strong";
export type OverviewRespondentStance =
  | "supports_view"
  | "challenges_view"
  | "mixed"
  | "not_addressed";

export interface OverviewRespondentEvidence {
  session_id: string;
  stance: OverviewRespondentStance;
  evidence_strength: OverviewEvidenceStrength;
  quotes: StudyQuote[];
  note: string;
}

export interface OverviewEvidenceItem {
  stance: OverviewEvidenceStance;
  summary: string;
  quotes: StudyQuote[];
  respondent_count: number;
}

export interface KeyQuestionOverview {
  key_question_id: string;
  current_view_headline: string;
  current_view: string;
  evidence_pattern: OverviewEvidencePattern;
  evidence_state: OverviewEvidenceState;
  addressed_respondent_count: number;
  respondent_evidence: OverviewRespondentEvidence[];
  evidence_items: OverviewEvidenceItem[];
  implication: string;
  open_item: string;
}

export interface StudyOverviewContent {
  version: 1;
  key_questions: KeyQuestionOverview[];
}

export interface StudyOverviewResponse {
  study_id: string;
  status: ReportStatus;
  overview: StudyOverviewContent | null;
  error: string | null;
  error_code: string | null;
  generated_at: string | null;
  source_session_count: number;
  is_stale: boolean;
  progress?: GenerationProgress | null;
}

/** The latest Hypotheses artifact, or null when generation has never been requested. */
export async function getStudyOverview(studyId: string): Promise<StudyOverviewResponse | null> {
  const resp = await apiRequest(`/studies/${studyId}/overview`);
  if (resp.status === 404) return null;
  return json(resp);
}

// ── v5 research: reader, secondary tab, and admin controls (mirrors backend schemas) ────

export type ResearchEvidenceKind = "interview" | "web";
export type ResearchAnswerStatus =
  | "open"
  | "answered"
  | "contested"
  | "insufficient"
  | "not_applicable"
  | "unavailable";
export type ResearchEvidenceState = "open" | "emerging" | "converging" | "contested";
export type ResearchConclusionDirection = "supports" | "challenges";
export type ResearchCounterpointSearchState = "found" | "searched_no_result" | "not_current";
export type ResearchLane = "combined" | "primary" | "secondary";

/** One display-ready accepted evidence item (server-resolved; ids are opaque handles). */
export interface ResearchEvidenceItem {
  kind: ResearchEvidenceKind;
  evidence_id: string;
  quote: string;
  session_id: string | null;
  turn_index?: number | null;
  title: string;
  publisher: string;
  published_date: string | null;
  url: string;
  /** Immutable whole-source preview shown when a Findings reference is hovered. */
  screenshot_id?: string | null;
}

/** A themed cluster of a lane's evidence: the model names it, the server counts it. */
export interface ResearchEvidenceGroupView {
  theme_id?: string;
  label: string;
  summary: string;
  relation: "supports" | "challenges";
  items: ResearchEvidenceItem[];
  respondent_count: number;
  work_count: number;
  /** @deprecated Use work_count. */
  publisher_count: number;
}

/** One complete lane view; the filter selects a projection wholesale, never remixes. */
export interface KeyQuestionProjectionView {
  /** Concise scorecard copy; historical snapshots fall back to statement server-side. */
  headline?: string;
  statement: string;
  status: ResearchAnswerStatus;
  confidence: "high" | "medium" | "low";
  evidence_state: ResearchEvidenceState;
  conclusion_direction: ResearchConclusionDirection;
  eligible_respondent_count: number;
  addressed_respondent_count: number;
  independent_work_count: number;
  /** @deprecated Use independent_work_count. */
  independent_publisher_count: number;
  counterpoint_search_state: ResearchCounterpointSearchState;
  implication: string;
  /** One authored, lane-specific uncertainty or next diligence question. */
  open_item?: string;
  supporting: ResearchEvidenceItem[];
  challenging: ResearchEvidenceItem[];
  /** Empty on pre-grouping snapshots and on any lane whose grouping failed validation; the
   *  card falls back to the flat `supporting`/`challenging` lists. */
  groups: ResearchEvidenceGroupView[];
  limitations: string[];
  open_items: string[];
}

export interface KeyQuestionResearchView {
  key_question_id: string;
  /** Authored label frozen into this exact Research snapshot. */
  question: string;
  combined: KeyQuestionProjectionView;
  primary: KeyQuestionProjectionView;
  secondary: KeyQuestionProjectionView;
  /**
   * Whether prose was authored for this question. Questions are authored independently, so a
   * snapshot can publish with one incomplete rather than discarding the whole run.
   */
  authoring_status?: "authored" | "failed";
}

export interface ResearchSnapshotView {
  version: number;
  /** Scope vocabulary frozen with this snapshot: v1 = Key question, v2+ = Hypothesis. */
  scope_schema_version: number;
  snapshot_mode: "primary_only" | "mixed" | "full";
  research_as_of: string;
  generated_at: string | null;
  key_questions: KeyQuestionResearchView[];
}

export interface ResearchAttemptStatus {
  public_attempt_id: string;
  status: ReportStatus;
  error: string | null;
  error_code: string | null;
  progress?: GenerationProgress | null;
}

export interface StudyResearchResponse {
  study_id: string;
  attempt: ResearchAttemptStatus | null;
  snapshot: ResearchSnapshotView | null;
  is_stale: boolean;
  stale_reasons: ResearchStaleReason[];
}

export interface SourcePassage {
  evidence_id: string;
  passage: string;
  key_question_ids: string[];
  relation: "supports" | "challenges";
  limitation: string;
}

export interface PublishedSource {
  source_id: string;
  title: string;
  publisher: string;
  published_date: string | null;
  url: string;
  screenshot_id: string | null;
  key_question_ids: string[];
  position: "supports" | "challenges" | "mixed";
  passages: SourcePassage[];
}

export interface StudySourcesResponse {
  study_id: string;
  attempt: ResearchAttemptStatus | null;
  version: number | null;
  /** Scope vocabulary and labels belonging to the last-good Sources snapshot. */
  scope_schema_version: number | null;
  key_questions: StudyKeyQuestion[];
  generated_at: string | null;
  sources: PublishedSource[];
  is_stale: boolean;
  /** Never includes `interviews_changed`: web research reads no interview. */
  stale_reasons: ResearchStaleReason[];
}

export interface StudyHypothesesResponse {
  study_id: string;
  attempt: ResearchAttemptStatus | null;
  snapshot: ResearchSnapshotView | null;
  is_stale: boolean;
  stale_reasons: ResearchStaleReason[];
}

export type ResearchStaleReason =
  | "scope_changed"
  | "interviews_changed"
  | "responses_changed"
  | "sources_not_current"
  | "generator_changed";

export async function getStudySources(studyId: string): Promise<StudySourcesResponse> {
  return json(await apiRequest(`/studies/${studyId}/sources`));
}

export async function generateStudySources(studyId: string): Promise<StudySourcesResponse> {
  return json(await apiRequest(`/studies/${studyId}/sources/generate`, { method: "POST" }));
}

export async function cancelStudySources(studyId: string): Promise<StudySourcesResponse> {
  return json(await apiRequest(`/studies/${studyId}/sources/cancel`, { method: "POST" }));
}

export async function getStudyHypotheses(studyId: string): Promise<StudyHypothesesResponse> {
  return json(await apiRequest(`/studies/${studyId}/hypotheses`));
}

export async function generateStudyHypotheses(studyId: string): Promise<StudyHypothesesResponse> {
  return json(await apiRequest(`/studies/${studyId}/hypotheses/generate`, { method: "POST" }));
}

export async function cancelStudyHypotheses(studyId: string): Promise<StudyHypothesesResponse> {
  return json(await apiRequest(`/studies/${studyId}/hypotheses/cancel`, { method: "POST" }));
}

export type ScopeDocumentFormat = "docx" | "pdf";

/** The draft scope (research context, hypotheses, interview guide) as a Word or PDF document. */
export async function downloadScopeDocument(
  studyId: string,
  format: ScopeDocumentFormat
): Promise<DownloadedFile> {
  const resp = await apiRequest(`/studies/${studyId}/scope/export.${format}`);
  if (!resp.ok) {
    let detail: unknown;
    try {
      detail = ((await resp.json()) as { detail?: unknown })?.detail;
    } catch {
      // no JSON body
    }
    throw new StudiesRequestError(resp.status, detail);
  }
  return {
    blob: await resp.blob(),
    filename: attachmentFilename(resp.headers.get("content-disposition"), `scope.${format}`),
  };
}
